import React, { useState } from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQForm from '../../components/form';
import UQInput from '../../components/input';
import UQCode from '../../components/code';
import UQSpacer from '../../components/spacer';
import UQButton from '../../components/button';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';
import UQGrid from '../../components/grid';

const InputPage = () => {
    const [formData, setFormData] = useState({ name: '', email: '' });
    const [errors, setErrors] = useState({ name: '', email: '' });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const newErrors = { name: '', email: '' };
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.email) newErrors.email = 'Email is required';

        setErrors(newErrors);

        if (!newErrors.name && !newErrors.email) {
            console.log('Form submitted:', formData);
        }
    };

    const attributes = [
        {
            attribute: 'label',
            type: 'string',
            default: 'undefined',
            description: 'The label text for the input field.',
        },
        {
            attribute: 'name',
            type: 'string',
            default: 'undefined',
            description: 'The name of the input field.',
        },
        {
            attribute: 'placeholder',
            type: 'string',
            default: 'undefined',
            description: 'The placeholder text for the input field.',
        },
        {
            attribute: 'value',
            type: 'string',
            default: 'undefined',
            description: 'The current value of the input field.',
        },
        {
            attribute: 'onChange',
            type: '(e: React.ChangeEvent<HTMLInputElement>) => void',
            default: 'undefined',
            description: 'Callback function triggered when the input value changes.',
        },
        {
            attribute: 'error',
            type: 'string',
            default: 'undefined',
            description: 'The error message to display below the input field.',
        },
        {
            attribute: 'required',
            type: 'boolean',
            default: 'false',
            description: 'Indicates whether the input field is required.',
        },
        {
            attribute: 'boldLabel',
            type: 'boolean',
            default: 'false',
            description: 'Makes the label text bold.',
        },
    ];

    const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
        {
            columnId: 'attribute',
            renderHeaderCell: () => 'Attribute',
            renderCell: (item) => item.attribute,
        },
        {
            columnId: 'type',
            renderHeaderCell: () => 'Type',
            renderCell: (item) => <code>{item.type}</code>,
        },
        {
            columnId: 'default',
            renderHeaderCell: () => 'Default',
            renderCell: (item) => <code>{item.default}</code>,
        },
        {
            columnId: 'description',
            renderHeaderCell: () => 'Description',
            renderCell: (item) => item.description,
        },
    ];

    const codeSnippet = `
import React, { useState } from 'react';
import { UQForm, UQInput, UQButton, UQGrid } from '@uniqconsulting/ui';

const MyForm = () => {
    const [formData, setFormData] = useState({ name: '', email: '' });
    const [errors, setErrors] = useState({ name: '', email: '' });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const newErrors = { name: '', email: '' };
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.email) newErrors.email = 'Email is required';

        setErrors(newErrors);

        if (!newErrors.name && !newErrors.email) {
            console.log('Form submitted:', formData);
        }
    };

    return (
        <UQForm
            onSubmit={handleSubmit}
            bordered
            backgroundColor="var(--Neutrals-100)"
            borderRadius="8px"
            footer={
                <UQGrid gap="1rem" justify="end">
                    <UQButton type="secondary" size="large">Cancel</UQButton>
                    <UQButton type="primary" size="large">Submit</UQButton>
                </UQGrid>
            }
            footerAlign="right"
        >
            <UQInput
                label="Name"
                name="name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                required
            />
            <UQInput
                label="Email"
                name="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
            />
        </UQForm>
    );
};
`;

    return (
        <>
            <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Input']} />
            <h1>UQInput Demo</h1>
            <UQSpacer size="m" />

            {/* Description */}
            <h2>Description</h2>
            <p>
                The <code>UQInput</code> component is a versatile input field that supports labels, placeholders, error messages, and more. It is designed to be flexible and easy to use in forms.
            </p>
            <UQSpacer size="m" />

            {/* Attributes Table */}
            <h2>Attributes</h2>
            <UQSpacer size="s" />
            <UQDataGrid
                items={attributes}
                columns={attributeColumns}
                sortable={false}
                selectionMode="none"
            />
            <UQSpacer size="m" />

            {/* Form Demo */}
            <h2>Form Example</h2>
            <p>
                This example demonstrates how to use <code>UQInput</code> with a form. It includes validation for required fields and displays error messages when the fields are empty.
            </p>
            <UQSpacer size="s" />
            <UQForm
                onSubmit={handleSubmit}
                bordered
                backgroundColor="var(--Neutrals-100)"
                borderRadius="8px"
                footer={
                    <UQGrid gap="1rem" justify="end">
                        <UQButton type="secondary" size="large">Cancel</UQButton>
                        <UQButton type="primary" size="large">Submit</UQButton>
                    </UQGrid>
                }
                footerAlign="right"
            >
                <UQInput
                    label="Name"
                    name="name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                />
                <UQInput
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                />
            </UQForm>
            <UQSpacer size="m" />

            {/* Code Example */}
            <h2>Code Example</h2>
            <p>
                Below is the code snippet for the form example shown above. You can copy it and use it in your project.
            </p>
            <UQSpacer size="s" />
            <UQCode code={codeSnippet} enableCopy />
        </>
    );
};

export default InputPage;